import { cn } from '@/shadcn/lib/utils';
import { Button as ShadcnButton } from '@/shadcn/components/ui/button';
import { MOTION, LIFT } from './motion';

/**
 * Roly tlacidiel - 4 kanonicke + legacy aliasy.
 *   primary   - zlate, hlavna akcia na stranke (jedna na obrazovku)
 *   secondary - cierne, vedlajsia akcia
 *   ghost     - seda, terciarna / zrusit
 *   icon      - kruhove, len ikona
 * Legacy: 'black' = secondary, 'grey' = ghost, 'primary-outlined' = gradientna varianta.
 */
type ButtonColor =
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'icon'
    | 'primary-outlined'
    | 'black'
    | 'grey';

interface Props extends React.ComponentProps<typeof ShadcnButton> {
    color?: ButtonColor;
    iconLeft?: React.ReactNode;
}

const COLOR_VARIANTS: Record<ButtonColor, string> = {
    primary: `
        bg-[var(--cust-primary)]
        text-black
        border-[var(--cust-primary)]
        hover:bg-[var(--cust-primary-hover)]
        hover:border-[var(--cust-primary-hover)]
        hover:shadow-[0_6px_18px_-6px_rgba(0,0,0,0.35)]
        ${LIFT}
    `,
    secondary: `
        bg-black
        text-white
        border-black
        hover:bg-[var(--cust-black-hover)]
        hover:border-[var(--cust-black-hover)]
        hover:shadow-[0_6px_18px_-6px_rgba(0,0,0,0.45)]
        ${LIFT}
    `,
    ghost: `
        bg-[var(--cust-grey)]
        text-white
        border-[var(--cust-grey)]
        hover:bg-[var(--cust-grey-hover)]
        hover:border-[var(--cust-grey-hover)]
        ${LIFT}
    `,
    icon: `
        bg-black/10
        text-black
        border-transparent
        rounded-full aspect-square p-0
        hover:bg-black/20
    `,
    'primary-outlined': `
        bg-gradient-to-r from-[#f7dc56] via-[#f3d33a] to-[#eec91c]
        text-black text-[13px]
        border-[#cdbe33]
        hover:from-[#f3d33a] hover:via-[#eec91c] hover:to-[#e0bb0f]
        hover:shadow-[0_6px_18px_-6px_rgba(0,0,0,0.35)]
        ${LIFT}
    `,
    // legacy aliasy - mapuju sa na rovnaky vzhlad ako nove roly
    black: `
        bg-black
        text-white
        border-black
        hover:bg-[var(--cust-black-hover)]
        hover:border-[var(--cust-black-hover)]
        hover:shadow-[0_6px_18px_-6px_rgba(0,0,0,0.45)]
        ${LIFT}
    `,
    grey: `
        bg-[var(--cust-grey)]
        text-white
        border-[var(--cust-grey)]
        hover:bg-[var(--cust-grey-hover)]
        hover:border-[var(--cust-grey-hover)]
        ${LIFT}
    `,
};

export function Button({ color = 'primary', className, iconLeft, ...props }: Props) {
    return (
        <ShadcnButton
            className={cn(
                'min-h-[40px] rounded-[30px] border text-[11px] font-[600] px-[16px]',
                'xl:min-h-[45px] xl:px-[19px] xl:text-[12px]',
                'relative z-[2] cursor-pointer gap-[5px] flex items-center',
                MOTION,
                'disabled:opacity-50 disabled:pointer-events-none',
                COLOR_VARIANTS[color],
                className,
            )}
            {...props}
        >
            {iconLeft && <span className="flex items-center">{iconLeft}</span>}
            {props.children}
        </ShadcnButton>
    );
}
